import { ConnectionState } from '../transport/client';
import { CapabilityReport } from '../probe/capabilities';

export class HudOverlay {
  private serverBadge: HTMLElement;
  private phoneBadge: HTMLElement;
  private sessionBadge: HTMLElement;
  private streamBadge: HTMLElement;
  private rttMetric: HTMLElement;
  private fpsMetric: HTMLElement;
  private decoderMetric: HTMLElement;
  private transportMetric: HTMLElement | null;
  private pairingModal: HTMLElement;
  private pairingError: HTMLElement;
  private codeInputs: HTMLInputElement[];
  private submitPairBtn: HTMLButtonElement;
  private capabilityDump: HTMLElement;
  private drawerToggle: HTMLElement;
  private diagnosticDrawer: HTMLElement;

  private onPairSubmitCallback?: (code: string) => Promise<boolean>;
  private onUnpairCallback?: () => void;
  private unpairBtn: HTMLButtonElement;
  private transportBtn: HTMLButtonElement | null;

  constructor() {
    this.serverBadge = document.getElementById('hud-server-status')!;
    this.phoneBadge = document.getElementById('hud-phone-status')!;
    this.sessionBadge = document.getElementById('hud-session-status')!;
    this.streamBadge = document.getElementById('hud-stream-status')!;
    this.rttMetric = document.getElementById('hud-rtt')!;
    this.fpsMetric = document.getElementById('hud-fps')!;
    this.decoderMetric = document.getElementById('hud-decoder')!;
    this.transportMetric = document.getElementById('hud-transport');
    this.pairingModal = document.getElementById('pairing-modal')!;
    this.pairingError = document.getElementById('pairing-error')!;
    this.submitPairBtn = document.getElementById('btn-submit-pair') as HTMLButtonElement;
    this.unpairBtn = document.getElementById('btn-unpair') as HTMLButtonElement;
    this.transportBtn = document.getElementById('btn-switch-transport') as HTMLButtonElement | null;
    this.capabilityDump = document.getElementById('capability-dump')!;
    this.drawerToggle = document.getElementById('drawer-toggle')!;
    this.diagnosticDrawer = document.getElementById('diagnostic-drawer')!;

    this.codeInputs = Array.from(document.querySelectorAll('.code-digit')) as HTMLInputElement[];

    this.setupEventListeners();
  }

  private setupEventListeners() {
    // 6-digit input auto-tabbing, numeric filter, and paste support
    this.codeInputs.forEach((input, index) => {
      input.addEventListener('paste', (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData?.getData('text') || '';
        const digits = pastedData.replace(/\D/g, '').slice(0, 6);
        if (digits) {
          digits.split('').forEach((char, i) => {
            if (this.codeInputs[i]) {
              this.codeInputs[i].value = char;
            }
          });
          const focusIndex = Math.min(digits.length, this.codeInputs.length - 1);
          this.codeInputs[focusIndex].focus();
          this.updatePairButtonState();
        }
      });

      input.addEventListener('input', (e) => {
        const val = (e.target as HTMLInputElement).value.replace(/\D/g, '');
        input.value = val;
        if (val && index < this.codeInputs.length - 1) {
          this.codeInputs[index + 1].focus();
        }
        this.updatePairButtonState();
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && !input.value && index > 0) {
          this.codeInputs[index - 1].focus();
        } else if (e.key === 'Enter' && this.submitPairBtn && !this.submitPairBtn.disabled) {
          this.submitPairBtn.click();
        }
      });
    });

    this.submitPairBtn.addEventListener('click', async () => {
      const code = this.codeInputs.map((i) => i.value).join('');
      if (code.length === 6 && this.onPairSubmitCallback) {
        this.submitPairBtn.disabled = true;
        this.submitPairBtn.textContent = 'Verifying...';
        this.clearPairingError();

        const success = await this.onPairSubmitCallback(code);
        if (!success) {
          this.showPairingError('Invalid or expired code. Please check your phone.');
          this.submitPairBtn.disabled = false;
          this.submitPairBtn.textContent = 'Pair Browser';
          this.codeInputs[0].focus();
        } else {
          this.hidePairingModal();
        }
      }
    });

    this.unpairBtn.addEventListener('click', () => {
      this.onUnpairCallback?.();
    });

    if (this.transportBtn && (window.location.port === '8080' || window.location.protocol === 'http:')) {
      this.transportBtn.textContent = '☁️ Cloud Relay';
      this.transportBtn.classList.remove('hidden');
      this.transportBtn.addEventListener('click', () => {
        sessionStorage.setItem('drivedeck_stay_on_relay', 'true');
        window.location.href = 'https://drivedeck.tommasoberti.com';
      });
    }

    // Drawer toggle
    this.drawerToggle.addEventListener('click', () => {
      this.diagnosticDrawer.classList.toggle('collapsed');
    });
  }

  private updatePairButtonState() {
    const code = this.codeInputs.map((i) => i.value).join('');
    this.submitPairBtn.disabled = code.length !== 6;
  }

  public onPairSubmit(cb: (code: string) => Promise<boolean>) {
    this.onPairSubmitCallback = cb;
  }

  public onUnpair(cb: () => void) {
    this.onUnpairCallback = cb;
  }

  public showLocalSwitchPrompt(localHttpUrl: string) {
    if (this.transportBtn && window.location.protocol === 'https:') {
      this.transportBtn.textContent = '⚡ Passa a Locale';
      this.transportBtn.classList.remove('hidden');
      this.transportBtn.classList.add('local-mode');
      this.transportBtn.onclick = () => {
        sessionStorage.removeItem('drivedeck_stay_on_relay');
        window.location.replace(localHttpUrl);
      };
    }
  }

  public showPairingModal() {
    this.pairingModal.classList.remove('hidden');
    this.codeInputs.forEach((i) => (i.value = ''));
    this.clearPairingError();
    this.codeInputs[0].focus();
    this.updatePairButtonState();
  }

  public hidePairingModal() {
    this.pairingModal.classList.add('hidden');
  }

  public showPairingError(msg: string) {
    this.pairingError.textContent = msg;
    this.pairingError.classList.remove('hidden');
  }

  public clearPairingError() {
    this.pairingError.textContent = '';
    this.pairingError.classList.add('hidden');
  }

  public setServerState(state: ConnectionState) {
    this.serverBadge.className = 'hud-badge';
    if (state === 'connected') {
      this.serverBadge.textContent = 'SERVER: ONLINE';
      this.serverBadge.classList.add('status-connected');
    } else if (state === 'reconnecting' || state === 'connecting') {
      this.serverBadge.textContent = `SERVER: ${state.toUpperCase()}...`;
      this.serverBadge.classList.add('status-warning');
    } else {
      this.serverBadge.textContent = 'SERVER: OFFLINE';
      this.serverBadge.classList.add('status-disconnected');
    }
  }

  public setPhoneState(connected: boolean) {
    this.phoneBadge.className = 'hud-badge';
    if (connected) {
      this.phoneBadge.textContent = 'PHONE: CONNECTED';
      this.phoneBadge.classList.add('status-connected');
    } else {
      this.phoneBadge.textContent = 'PHONE: DISCONNECTED';
      this.phoneBadge.classList.add('status-disconnected');
    }
  }

  public setSessionState(paired: boolean) {
    this.sessionBadge.className = 'hud-badge';
    if (paired) {
      this.sessionBadge.textContent = 'BROWSER: PAIRED';
      this.sessionBadge.classList.add('status-connected');
      this.hidePairingModal();
      this.unpairBtn.classList.remove('hidden');
    } else {
      this.sessionBadge.textContent = 'BROWSER: UNPAIRED';
      this.sessionBadge.classList.add('status-disconnected');
      this.showPairingModal();
      this.unpairBtn.classList.add('hidden');
    }
  }

  public setStreamState(active: boolean) {
    this.streamBadge.className = 'hud-badge';
    if (active) {
      this.streamBadge.textContent = 'STREAM: LIVE';
      this.streamBadge.classList.add('status-connected');
    } else {
      this.streamBadge.textContent = 'STREAM: OFFLINE';
      this.streamBadge.classList.add('status-disconnected');
    }
  }

  public setRtt(rttMs: number) {
    this.rttMetric.textContent = `${rttMs} ms`;
    if (rttMs > 150) {
      this.rttMetric.style.color = '#ff7b72';
    } else if (rttMs > 75) {
      this.rttMetric.style.color = '#e3b341';
    } else {
      this.rttMetric.style.color = '#56d364';
    }
  }

  public setFps(fps: number) {
    this.fpsMetric.textContent = `${fps}`;
  }

  public setDecoderName(name: string) {
    this.decoderMetric.textContent = name;
  }

  public setTransportMode(mode: 'local' | 'webrtc' | 'relay') {
    if (this.transportMetric) {
      if (mode !== 'relay') {
        this.transportMetric.textContent = mode === 'webrtc' ? 'LOCAL WEBRTC (DIRECT)' : 'LOCAL WI-FI (DIRECT)';
        this.transportMetric.style.color = '#34d399';
      } else {
        this.transportMetric.textContent = 'VPS RELAY';
        this.transportMetric.style.color = '#38bdf8';
      }
    }
  }

  public displayCapabilityReport(report: CapabilityReport) {
    this.capabilityDump.textContent = JSON.stringify(report, null, 2);
    this.setDecoderName(report.selectedRenderer.toUpperCase());
  }
}
